import React, { useState } from "react";
import { DateRange, Range } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

export interface FiltersState {
    orderId?: string;
    tableQuery?: string;
    status?: string;
    payment?: string;
    minAmount?: string;
    maxAmount?: string;
    customerQuery?: string;
    dateFrom?: string;
    dateTo?: string;
}

interface OrderFiltersProps {
    pendingFilters: FiltersState;
    setPendingFilters: (filters: FiltersState) => void;
    applyFilters: () => void;
    resetFilters: () => void;
}

export const OrderFilters: React.FC<OrderFiltersProps> = ({
    pendingFilters,
    setPendingFilters,
    applyFilters,
    resetFilters,
}) => {
    const [dateRange, setDateRange] = useState<Range[]>([
        { startDate: undefined, endDate: undefined, key: "selection" },
    ]);
    const [showCalendar, setShowCalendar] = useState(false);

    const formatRange = () => {
        if (dateRange[0].startDate && dateRange[0].endDate) {
            return `${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`;
        }
        return "Select Dates";
    };

    const handleDateChange = (item: any) => {
        const selection = item.selection as Range;
        setDateRange([selection]);
        setPendingFilters({
            ...pendingFilters,
            dateFrom: selection.startDate?.toLocaleDateString().split("T")[0],
            dateTo: selection.endDate?.toLocaleDateString().split("T")[0],
        });
        setShowCalendar(false);
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 mb-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {[
                    { label: "Order ID", key: "orderId", type: "text", placeholder: "#1234" },
                    { label: "Table", key: "tableQuery", type: "text", placeholder: "Label or number" },
                    { label: "Status", key: "status", type: "select", options: ["", "Accepted", "Completed", "Cancelled"] },
                    { label: "Payment", key: "payment", type: "select", options: ["", "paid", "pending"] },
                    { label: "Min Amount", key: "minAmount", type: "number", placeholder: "0" },
                    { label: "Max Amount", key: "maxAmount", type: "number", placeholder: "1000" },
                    { label: "Customer", key: "customerQuery", type: "text", placeholder: "Name or contact" },
                ].map((field) => (
                    <div key={field.key} className="flex flex-col">
                        <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">{field.label}</label>
                        {field.type === "select" ? (
                            <select
                                value={(pendingFilters as any)[field.key] || ""}
                                onChange={(e) => setPendingFilters({ ...pendingFilters, [field.key]: e.target.value })}
                                className="mt-1 border rounded px-2 py-1 text-xs sm:text-sm dark:bg-gray-700 dark:text-white"
                            >
                                {field.options!.map((opt) => (
                                    <option key={opt} value={opt}>
                                        {opt || "All"}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type={field.type}
                                value={(pendingFilters as any)[field.key] || ""}
                                onChange={(e) => setPendingFilters({ ...pendingFilters, [field.key]: e.target.value })}
                                placeholder={field.placeholder}
                                className="mt-1 border rounded px-2 py-1 text-xs sm:text-sm dark:bg-gray-700 dark:text-white"
                            />
                        )}
                    </div>
                ))}

                {/* Date Range */}
                <div className="flex flex-col relative">
                    <label className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">Date Range</label>
                    <button
                        onClick={() => setShowCalendar((s) => !s)}
                        className="mt-1 border rounded px-2 py-1 text-xs sm:text-sm text-left dark:bg-gray-700 dark:text-white"
                    >
                        {formatRange()}
                    </button>
                    {showCalendar && (
                        <div className="absolute z-20 mt-2 bg-white shadow rounded-lg dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                            <DateRange
                                editableDateInputs={true}
                                onChange={handleDateChange}
                                moveRangeOnFirstSelection={false}
                                ranges={dateRange}
                                maxDate={new Date()}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-2 flex gap-2 justify-end">
                <button
                    onClick={() => {
                        resetFilters(); // reset the parent filters
                        setDateRange([{ startDate: undefined, endDate: undefined, key: "selection" }]); // reset internal date
                        setShowCalendar(false); // close calendar if open
                    }}
                    className="px-3 py-1.5 text-xs sm:text-sm border rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    Reset
                </button>
                <button
                    onClick={applyFilters}
                    className="px-3 py-1.5 text-xs sm:text-sm border rounded-md bg-orange-500 text-white hover:bg-orange-600"
                >
                    Apply
                </button>
            </div>
        </div>
    );
};
